import React, { useState } from 'react'
import {
  Box,
  CircularProgress,
  FormLabel,
  Grid,
  Modal,
  styled,
  TextField,
  Typography,
  Button,
  DialogActions,
  Fade,
  IconButton,
  DialogContent,
  DialogTitle,
  Dialog,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import 'dayjs/locale/fr'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleExclamation,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { BorderButtonPurple, CustomButton } from '../buttons'
import lightTheme from '../../styles/theme/lightTheme'
import '../../styles/components/_box.scss'
import '../../styles/components/_grids.scss'
import '../../styles/components/_inputs.scss'
import Api from '../../services/api.service'
import { handlingErrors } from '../../utils'
import { Slide, ToastContainer, toast } from 'react-toastify'
import { faCircleCheck, faClose } from '@fortawesome/pro-solid-svg-icons'
import { SelectInputBasic } from '../inputs'
import { TYPE_RDV } from '../../data'
import { useTranslation } from 'react-i18next'
import OutlineButton from '../buttons/OutlineButton'
import GradientButton from '../buttons/GradientButton'

const ModalContainer = styled(Box)(({ theme }) => ({
  width: 'calc(100% - 32px)',
  maxWidth: 800,
  minHeight: 400,
  maxHeight: '90vh',
  backgroundColor: '#FFFFFF',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  borderRadius: '16px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',

  '&:focus-visible': {
    outline: 'none',
  },

  [theme.breakpoints.up('sm')]: {
    width: 800,
    padding: '20px 40px',
  },
}))

const BoxContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
}))

const AddEvent = ({ open, onClose, onSave, role, id }) => {
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [errorTime, setErrorTime] = useState(false)
  const [errorType, setErrorType] = useState(false)
  const [dateValue, setDateValue] = useState(null)

  const [rdvSelect, setRdvSelect] = useState('')

  const handleRdvSelect = (event) => {
    const inputValue = event.target.value
    setRdvSelect(inputValue)
  }
  const changeDate = (dateValue) => {
    setDateValue(dateValue.target.value)
  }

  const submitNewDate = async () => {
    setErrorTime(false)
    setErrorType(false)
    if (!dateValue) setErrorTime(true)
    if (!rdvSelect) setErrorType(true)
    if (!dateValue || !rdvSelect) return
    setLoading(true)

    let data = {
      dateDebut: dateValue,
      dateFin: dateValue,
      type: rdvSelect,
      detail: 'Demande RDV',
    }
    if (role === 'ROLE_CANDIDAT') data.candidat = '/api/candidats/' + id
    if (role === 'ROLE_ENTREPRISE') data.entreprise = '/api/entreprises/' + id

    try {
      const result = await Api.calendar.add(data)
      const res = handlingErrors(result)

      if (res.ok) {
        toast.success(t('successMessage.appointmentRequestSent'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleCheck}
              fontSize={25}
              color={lightTheme.palette.success.main}
            />
          ),
        })
        onSave()
      }

      if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
        window.scrollTo(0, 0)
        return
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
    }
    setLoading(false)
  }
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : '16px',
            m: fullScreen ? 0 : undefined,
            maxHeight: fullScreen ? '100%' : 'auto',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            pb: 2,
            flexShrink: 0,
            borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
            backgroundColor: 'rgba(249, 250, 251, 0.7)',
          }}
        >
          <Typography
            variant={fullScreen ? 'subtitle1' : 'h6'}
            sx={{ fontWeight: 600, color: '#1f2937', pr: 1, lineHeight: 1.3 }}
          >
            {t('profile.appointmentRequest')}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                color: '#374151',
              },
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: { xs: 2, sm: 4 },
            pt: '32px !important',
            backgroundColor: 'rgba(249, 250, 251, 0.65)',
          }}
        >
          <Box
            sx={{
              width: '100%',
              backgroundColor: '#fff',
              borderRadius: 3,
              boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
              p: { xs: 2, sm: 3 },
            }}
          >
            <form>
              <Grid container spacing={2}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={i18n.language}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <FormLabel className={'labelForm'}>
                      {t('list.type')}
                    </FormLabel>

                    <SelectInputBasic
                      items={TYPE_RDV[i18n.language]}
                      value={rdvSelect}
                      handleChange={handleRdvSelect}
                      type={'border'}
                      sx={{ minheight: 52 }}
                    />

                    {errorType && (
                      <Typography className={'error-message'}>
                        {t('errorMessage.appointment')}
                      </Typography>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <FormLabel className={'labelForm'}>
                      {t('profile.date')}
                    </FormLabel>
                    <TextField
                      fullWidth
                      variant="outlined"
                      sx={{
                        '& .MuiInputBase-root': {
                          borderRadius: 2,
                          backgroundColor: '#F8FBFF',
                          minHeight: 52,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E2E8F0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#CBD5E1',
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: lightTheme.palette.primary.main,
                        },
                      }}
                      type="datetime-local"
                      onChange={changeDate}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 900,
                      }}
                    />

                    {errorTime && (
                      <Typography className={'error-message'}>
                        {t('errorMessage.date')}
                      </Typography>
                    )}
                  </Grid>
                </LocalizationProvider>
              </Grid>
            </form>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 4 },
            py: { xs: 3, sm: 4 },
            gap: 2,
            justifyContent: 'center',
            backgroundColor: 'rgba(249, 250, 251, 0.8)',
            borderTop: '1px solid rgba(229, 231, 235, 0.8)',
            flexDirection: { xs: 'column', sm: 'row' },
            '& > *': {
              width: { xs: '100%', sm: 'auto' },
            },
          }}
        >
          <OutlineButton onClick={onClose} fullWidth={fullScreen}>
            {t('button.cancel')}
          </OutlineButton>

          <GradientButton onClick={submitNewDate} fullWidth={fullScreen} disabled={!dateValue || !rdvSelect}>
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                {t('profile.saving')}
              </>
            ) : (
              t('button.sendRequest')
            )}
          </GradientButton>
        </DialogActions>
      </Dialog>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        closeButton={false}
        transition={Slide}
        className={'toast'}
      />
    </>
  )
}

export default AddEvent
